import * as React from 'react';
import { useAuth } from '../../../App';
import { PatientTreatmentSearchApi } from '../../../api/PatientTreatmentSearchApi';
import { IPatientTreatmentSearchParams, IPatientTreatment } from '../../../models/IPatientTreatment';
import { SpinnerWrapper, PaginationNavigation, SortTableContext, AlertMessage, AlertType } from 'soprasteria-ui-library';
import { IPagination } from "soprasteria-ui-library/dist/models/IPagination"
import { PatientTreatmentSearchForm } from './PatientTreatmentSearchForm';
import { PatientTreatmentSearchTable } from './PatientTreatmentSearchTable';
import { SortOrder } from '../../../models/IPage';
import { useHistory } from 'react-router';
import { PATIENT_TREATMENT_SEARCH } from '../../../utils/RouteConstants';
import { setMessage } from '../../../helpers/MessageHelper';

export const PatientTreatmentSearch: React.FunctionComponent<{}> = () => {
    const auth = useAuth();
    const api = new PatientTreatmentSearchApi(auth);
    const [warningMessage, setWarningMessage] = React.useState<string>("");
    const abortController = new AbortController();
    const [isFetchingPatientTreatments, setIsFetchingPatientTreatments] = React.useState<boolean>(false);
    const [patientTreatments, setPatientTreatments] = React.useState<IPatientTreatment[]>();
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const pageSize = 20;
    const [searchParams, setSearchParams] = React.useState<IPatientTreatmentSearchParams>();
    const { sortOrder, selectedPropertyToSortBy, setSortOrder, setSelectedPropertyToSortBy } = React.useContext(SortTableContext);
    const defaultOrderBy = "TreatmentDate";
    const history = useHistory();
    const [totalCount, setTotalCount] = React.useState<number>(0);

    React.useEffect(() => {
        document.getElementById("clear-form")?.addEventListener("click", resetValue);

        if (setSelectedPropertyToSortBy && setSortOrder) {
            if (history.location.state !== undefined && history.location.state !== null) {
                const lastSearchParams = history.location.state as IPatientTreatmentSearchParams;
                setSelectedPropertyToSortBy(lastSearchParams.propertyToOrderBy ?? defaultOrderBy);
                setSortOrder(lastSearchParams.sortOrder ?? SortOrder.descending);
                searchPatientTreatments(lastSearchParams);
            }
            else {
                setSelectedPropertyToSortBy(defaultOrderBy);
                setSortOrder(SortOrder.descending);
                searchPatientTreatments({ pageSize: pageSize, pageNumber: 1, propertyToOrderBy: defaultOrderBy, sortOrder: SortOrder.descending, treatmentDateFrom: undefined });
            }
        }
        return () => {
            abortController.abort();
            document.getElementById("clear-form")?.removeEventListener("click", resetValue);
        }
    }, [])

    React.useEffect(() => {
        if (patientTreatments) {
            onSortOrderClick()
        }
    }, [selectedPropertyToSortBy, sortOrder])

    const resetValue = () => {
        history.push(PATIENT_TREATMENT_SEARCH, undefined);
    }

    const searchPatientTreatments = (searchParams: IPatientTreatmentSearchParams) => {
        setIsFetchingPatientTreatments(true);
        searchParams.pageSize = pageSize;
        searchParams.pageNumber = searchParams.pageNumber ?? 1;
        searchParams.propertyToOrderBy = searchParams.propertyToOrderBy ?? selectedPropertyToSortBy;
        searchParams.sortOrder = searchParams.sortOrder ?? sortOrder;
        history.push(PATIENT_TREATMENT_SEARCH, searchParams);

        api.searchPatientTreatments(searchParams, (response) => {
            setPatientTreatments(response.data);
            setPaginationHeader(response.paginationHeader);
            setTotalCount(response.paginationHeader.totalCount);
        }, undefined, () => setIsFetchingPatientTreatments(false));

        setSearchParams(searchParams);
    }

    const searchPatientTreatmentsToExcel = (searchParams: IPatientTreatmentSearchParams) => {
        setIsFetchingPatientTreatments(true);
        searchParams.pageSize = 100001;
        searchParams.pageNumber = 1;

        api.searchPatientTreatmentsAsExcel(searchParams, "Åtgärdskoder",
            undefined,
            (error) => {
                const message = setMessage(error, warningMessage);
                setWarningMessage(message);
            },
            () => setIsFetchingPatientTreatments(false));
    }

    const onPageSelect = (pageNumber: number) => {
        if (searchParams) {
            setIsFetchingPatientTreatments(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = pageNumber;
            history.push(PATIENT_TREATMENT_SEARCH, currentSearchParams);

            api.searchPatientTreatments(currentSearchParams, (response) => {
                setPatientTreatments(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, () => setIsFetchingPatientTreatments(false));
        }
    }

    const onSortOrderClick = () => {
        if (searchParams) {
            setIsFetchingPatientTreatments(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = 1;
            currentSearchParams.sortOrder = sortOrder;
            currentSearchParams.propertyToOrderBy = selectedPropertyToSortBy;
            history.push(PATIENT_TREATMENT_SEARCH, currentSearchParams);

            api.searchPatientTreatments(currentSearchParams, (response) => {
                setPatientTreatments(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, () => setIsFetchingPatientTreatments(false));
        }
    }

    return (
        <>
            <SpinnerWrapper isLoading={isFetchingPatientTreatments}>
                <section className="card">
                    <div className="card-header">
                        <h1>Sök åtgärdskoder</h1>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.warning} duration={3000}>{warningMessage}</AlertMessage>
                        <PatientTreatmentSearchForm searchPatientTreatments={searchPatientTreatments}
                            searchPatientTreatmentsToExcel={searchPatientTreatmentsToExcel}
                            preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as any : undefined}
                            disableExportToExcel={totalCount > 100000} />
                        {
                            patientTreatments ?
                                <>
                                    <PatientTreatmentSearchTable patientTreatments={patientTreatments} />
                                    <PaginationNavigation
                                        ariaLabel="received patient treament pagination selector"
                                        pagination={paginationHeader}
                                        pageNeighbours={2}
                                        selectedPage={page => onPageSelect(page)}
                                        showCount={true} />
                                </>
                                : null
                        }
                    </div>
                </section>
            </SpinnerWrapper>

        </>
    )
}
